import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  FaCalendarAlt,
  FaMale,
  FaFemale,
  FaRegCopy,
  // FaCheck,
} from "react-icons/fa";
import styles from "./DailyAttendanceCard.module.css";

const DailyAttendanceCard = ({
  title,
  icon = <FaCalendarAlt />,
  summary,
  absentees = [],
  handleClick,
  onCopy,
  copiedResetMs = 1500,
  onlyCard = false,
  totalLabel, // NEW: override the "Total Students" label (e.g. "Total Teachers")
}) => {
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  // Track which absentee's phone was just copied
  const [copiedId, setCopiedId] = useState(null);
  const copyTimerRef = useRef(null);

  // Cleanup on unmount to avoid setting state after unmount
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(
    async (phone, item) => {
      if (!phone) return;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(phone);
        } else {
          // Fallback for older browsers / non-secure contexts
          const textarea = document.createElement("textarea");
          textarea.value = phone;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        // Show "Copied!" feedback for this item
        setCopiedId(item.id);

        if (copyTimerRef.current) {
          clearTimeout(copyTimerRef.current);
        }
        copyTimerRef.current = setTimeout(() => {
          setCopiedId(null);
          copyTimerRef.current = null;
        }, copiedResetMs);

        if (typeof onCopy === "function") onCopy(phone, item);
      } catch (err) {
        // Silent fail — don't break UX if clipboard is unavailable
        console.error("Copy failed:", err);
      }
    },
    [onCopy, copiedResetMs],
  );

  const headerTitle = title || t.dailyAttendance;

  const tiles = [
    {
      key: "total",
      label: totalLabel || t.totalStudents, // CHANGED: prop wins, fallback unchanged
      className: styles.tileTotal,
      data: summary?.total,
    },
    {
      key: "present",
      label: t.present,
      className: styles.tilePresent,
      data: summary?.present,
    },
    {
      key: "absent",
      label: t.absent,
      className: styles.tileAbsent,
      data: summary?.absent,
    },
    {
      key: "leave",
      label: t.leave,
      className: styles.tileLeave,
      data: summary?.leave,
    },
  ];

  return (
    <div className="w-100">
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {icon && (
            <span className={styles.icon} aria-hidden="true">
              {icon}
            </span>
          )}
          <h3 className="chartTitle">{headerTitle}</h3>
        </div>
      </div>

      {/* KPI tiles */}
      <div className={styles.tileRow}>
        {tiles.map((tile) => (
          <div key={tile.key} className={`${styles.tile} ${tile.className}`}>
            <div className={styles.tileTop}>
              <span className={styles.tileCount}>{tile.data?.count ?? 0}</span>
              <span className={styles.tileLabel}>{tile.label}</span>
            </div>
            <div className={styles.tileGender}>
              <span className={styles.genderItem}>
                <FaMale className={styles.maleIcon} aria-hidden="true" />
                <span>{tile.data?.male ?? 0}</span>
              </span>
              <span className={styles.genderItem}>
                <FaFemale className={styles.femaleIcon} aria-hidden="true" />
                <span>{tile.data?.female ?? 0}</span>
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Today Absentee */}
      {onlyCard === false && (
        <div className={styles.absenteeWrap}>
          <p className={styles.absenteeHeading}>{t.todayAbsentee}</p>

          {absentees.length === 0 ? (
            <p className={styles.viewHint}>{t.noAbsenteeToday}</p>
          ) : (
            <div className={styles.absenteeGrid}>
              {absentees.map((item) => {
                const isCopied = copiedId === item.id;

                return (
                  <div key={item.id} className={styles.absenteeItem}>
                    <div className={styles.avatar} aria-hidden="true">
                      {item.initials}
                    </div>
                    <div className={styles.absenteeInfo}>
                      <p
                        className={styles.absenteeName}
                        onClick={() => handleClick?.(item.id)}
                        title={item.name}
                        style={{ cursor: "pointer" }}
                      >
                        {item.name}
                      </p>
                      {/* <div className={styles.phoneRow}>
                        <span className={styles.phoneText}>{item.phone}</span>

                        <button
                          type="button"
                          className={`${styles.copyBtn} ${isCopied ? styles.copyBtnSuccess : ""
                            }`}
                          onClick={() => handleCopy(item.phone, item)}
                          aria-label={`${t.copyPhoneAria} ${item.name}`}
                          title={isCopied ? t.copied : t.copyPhoneAria}
                        >
                          <FaRegCopy />
                       
                        </button>                     
                      </div> */}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyAttendanceCard;
